<template>
	<v-dialog
		v-model="dialog"
		width="1000"
		persistent
	>
		<template #activator="{ props }">
			<v-btn
				variant="text"
				block
				color="primary"
				v-bind="props"
			>
				<v-icon
					start
					icon="$send"
				/>
				<translate>
					Contact Customer
				</translate>
				...
			</v-btn>
			<v-snackbar
				v-model="snackbar"
				:color="snackbarColor"
				timeout="5000"
			>
				{{ $gettext(snackbarText) }}
			</v-snackbar>
		</template>
		<v-card>
			<v-card-title>
				<v-row
					no-gutters
					class="d-flex align-center"
				>
					<v-col>
						<translate>
							Contact Customer
						</translate>
					</v-col>
					<v-spacer class="flex-grow-1 flex-shrink-0" />

					<v-col class="flex-grow-0 flex-shrink-1">
						<v-btn
							variant="text"
							icon="$close"
							@click="dialog = false"
						/>
					</v-col>
				</v-row>
			</v-card-title>
			<v-card-text>
				<v-stepper
					v-model="step"
					non-linear
				>
					<v-stepper-header>
						<v-stepper-item
							:complete="step > 1"
							editable
							:value="1"
						>
							<translate>
								Select Email Template
							</translate>
						</v-stepper-item>

						<v-divider />

						<v-stepper-item
							:complete="step > 2"
							:editable="editable"
							:value="2"
						>
							<translate>
								Edit Message
							</translate>
						</v-stepper-item>
					</v-stepper-header>
					<v-stepper-window>
						<v-stepper-window-item :value="1">
							<v-row
								no-gutters
							>
								<v-col>
									<email-template-select
										:variables="templateVariable"
										height="443"
										@input="loadEditor"
									>
										<template #extra-selects>
											<v-col cols="2">
												<v-text-field
													v-model="languageName"
													variant="outlined"
													readonly
													hide-details
													:label="$gettext('Language')"
												/>
											</v-col>
											<v-col cols="2">
												<v-text-field
													v-model="domainName"
													variant="outlined"
													readonly
													hide-details
													:label="$gettext('Domain')"
												/>
											</v-col>
										</template>
									</email-template-select>
								</v-col>
							</v-row>
							<v-row
								no-gutters
							>
								<v-col>
									<v-btn
										variant="text"
										@click="dialog = false"
									>
										<translate>
											Close
										</translate>
									</v-btn>
								</v-col>
								<v-col
									class="text-right"
								>
									<v-btn
										color="primary"
										:disabled="!editable"
										@click="step = 2"
									>
										<translate>
											Continue
										</translate>
									</v-btn>
								</v-col>
							</v-row>
						</v-stepper-window-item>
						<v-stepper-window-item :value="2">
							<v-row>
								<v-col>
									<v-text-field
										v-model="subject"
										:label="$gettext('Subject')"
										variant="outlined"
									/>
								</v-col>
							</v-row>
							<v-row
								no-gutters
							>
								<v-col>
									<tiny-editor
										v-model="body"
									/>
								</v-col>
							</v-row>
							<v-row
								class="align-end"
								no-gutters
							>
								<v-col
									cols="4"
								>
									<v-file-input
										v-model="attachments"
										accept="image/*,.docx,.doc,.pdf"
										hide-details
										multiple
										:label="$gettext('Attachments') + '...'"
										variant="outlined"
									/>
								</v-col>
								<v-col
									class="text-right"
								>
									<v-btn
										color="primary"
										:loading="sending"
										:disabled="success"
										@click="sendMail"
									>
										<v-icon
											style="padding-right: 5px"
											icon="$send"
										/>
										<translate>
											Send
										</translate>
									</v-btn>
								</v-col>
							</v-row>
							<v-spacer />
						</v-stepper-window-item>
					</v-stepper-window>
				</v-stepper>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import EmailTemplateSelect from '../molecules/EmailTemplateSelect.vue';
import {
	axios,
} from '../../services/axios-service';
import TinyEditor from '../molecules/TinyEditor.vue';
import {
	debounce,
} from 'lodash';
export default {
	name: 'ManualCommunication',
	components: {
		TinyEditor, EmailTemplateSelect
	},

	props: {
		language: {
			type: Object,
			required: true,
		},
		domain: {
			type: Object,
			required: true,
		},
		recipientId: {
			type: Number,
			required: true,
		},
		orderId: {
			type: Number,
			required: false,
			default: null,
		},
		claimId: {
			type: Number,
			required: false,
			default: null,
		},
	},

	data () {
		return {
			step: 1,
			templateTypeId: null,
			template: null,
			languageName: this.language.name, // Avoid mutation of props
			languageId: this.language.id,
			domainId: this.domain.id,
			domainName: this.domain.name,
			body: '',
			sending: false,
			success: false,
			snackbar: false,
			editable: false,
			snackbarText: '',
			snackbarColor: 'primary',
			subject: '',
			dialog: false,
			attachments: [],
		};
	},

	computed: {
		templateVariable () {
			if (this.claimId) {
				return 'claim';
			}

			if (this.orderId) {
				return 'order';
			}

			return 'user';
		}
	},

	methods: {
		/**
		 * Render an email template into the HTML body to be given to tinymce
		 *
		 * @returns {object} rendered HTML body
		 * @param {object} template
		 */
		renderTemplate (template) {
			return new Promise((resolve, reject) => {
				axios.get('/api/v0/emailtemplates/render', {
					params: {
						templateId: template.id,
						userId: this.recipientId,
						orderId: this.orderId,
						claimId: this.claimId,
					}
				}).then((renderedResponse) => {
					resolve(JSON.parse(renderedResponse.data));
				}).catch(() => {
					reject(this.$gettext('Failed to render email template'));
				});
			});
		},

		/**
		 * Retrieve the relevant email template based on type id, domain and language
		 *
		 * @returns {object} email template
		 */
		getTemplate () {
			return new Promise((resolve, reject) => {
				axios.get('/api/v0/emailtemplates', {
					params: {
						emailtemplatetypeId: this.templateTypeId,
						domainId: this.domainId,
						languageId: this.languageId,
					}
				}).then((templateResponse) => {
					if (templateResponse.data.length > 0) {
						resolve(templateResponse.data[0]);
					}
					reject('No valid email template available');
				}).catch((error) => {
					reject(error.toJSON());
				});
			});
		},

		/**
		 * @param {string} message
		 * @param {boolean} isError
		 */
		displaySnackbar (message, isError = false) {
			this.snackbarText = message;
			this.snackbarColor = isError ? 'error' : 'primary';
			this.snackbar = true;
		},

		/**
		 * With a template type selected, load the html into the editor
		 *
		 * @param {object} templateType
		 */
		loadEditor: debounce(function (templateType) {
			this.templateTypeId = templateType;
			if (this.templateTypeId) {
				this.getTemplate().then((template) => {
					this.template = template;
					this.renderTemplate(template).then((renderedTemplate) => {
						this.body = renderedTemplate.body;
						this.subject = renderedTemplate.subject;
						this.editable = true;
					}).catch((error) => {
						this.displaySnackbar(error, true);
						this.editable = false;
					});
				}).catch((error) => {
					this.displaySnackbar(error, true);
					this.editable = false;
				});

			} else {
				this.editable = false;
			}
		}, 500),

		/**
		 * Send the email to the recipient with the manually edited subject and body.
		 */
		sendMail: debounce(function () {
			this.sending = true;
			const formData = new FormData();

			formData.append('body', this.body);
			formData.append('subject', this.subject);
			formData.append('recipient_id', `${this.recipientId}`);
			formData.append('template_id', `${this.template.id}`);
			formData.append('order_id', `${this.orderId}`);
			formData.append('claim_id', `${this.claimId}`);

			this.attachments.forEach((attachment, index) => {
				formData.append(`attachments[${index}]`, attachment, attachment.name);
			});

			axios.post('/api/communications/send', formData)
				.then(() => {
					this.success = true;
					this.body = '';
					this.displaySnackbar('Email Sent!', false);
					this.dialog = false;
					setTimeout(() => {
						window.location.reload();
					}, 300);
				}).catch((error) => {
					this.success = false;
					this.displaySnackbar('Failed to send email', true);
					window.captureException(error);
				}).finally(() => {
					this.sending = false;
				});
		})
	}
};
</script>
